<script lang="ts">
  import { orgPricing, userTier } from './stores';
  import { createCheckoutSession, createPortalSession } from './api';
  let { show = false, onClose }: { show: boolean; onClose: () => void } = $props();

  let loading = $state(false);
  let error = $state('');

  async function handleUpgrade(tier: number) {
    loading = true;
    error = '';
    try {
      // If already on a paid tier, plan changes must go through the Stripe portal —
      // checkout.Session.create with mode=subscription always creates a NEW sub.
      if ($userTier >= 2) {
        const { url } = await createPortalSession();
        window.location.href = url;
        return;
      }
      const { url } = await createCheckoutSession(tier);
      window.location.href = url;
    } catch (e: any) {
      // Backend 409 means an active sub already exists — fall back to portal.
      const msg = e?.message || '';
      if (msg.includes('409') || msg.includes('existing_subscription')) {
        try {
          const { url } = await createPortalSession();
          window.location.href = url;
          return;
        } catch (e2: any) {
          error = e2?.message || 'Could not open billing portal.';
        }
      } else {
        error = msg || 'Checkout failed — please try again.';
      }
      loading = false;
    }
  }
</script>

{#if show}
<div class="upgrade-overlay" onclick={onClose}>
  <div class="upgrade-modal" onclick={(e) => e.stopPropagation()}>
    <button class="upgrade-close" onclick={onClose}>&times;</button>
    <h2 style="margin-bottom:4px;">{$userTier >= 2 ? 'Change Your Plan' : 'Upgrade Your Plan'}</h2>
    <p class="text-dim" style="font-size:13px; margin-bottom:24px;">
      {$userTier >= 2 ? 'You\'ll be sent to the billing portal to switch plans.' : 'Choose the plan that fits your team.'}
    </p>

    {#if error}
      <p style="color: #ef4444; font-size: 12px; margin-bottom: 12px;">{error}</p>
    {/if}

    <div class="plan-grid">
      <div class="plan-card" class:plan-current={$userTier === 2}>
        <div class="plan-name">Base {#if $userTier === 2}<span class="plan-current-tag">Current</span>{/if}</div>
        <div class="plan-price">${$orgPricing.text}<span class="plan-period">/mo per seat</span></div>
        <ul class="plan-features">
          <li>Full CRM with email + calendar sync</li>
          <li>AI email drafting powered by your knowledge base + sales collateral</li>
          <li>Prospecting learned from your wins with live lead research</li>
          <li>Live deal quality tracking</li>
          <li>AI partner joins 5 client calls a month, shared across your team</li>
        </ul>
        <button class="plan-cta" onclick={() => handleUpgrade(2)} disabled={loading || $userTier === 2}>
          {loading ? 'Redirecting...' : ($userTier === 2 ? 'Current Plan' : ($userTier >= 2 ? 'Switch to Base' : 'Get Started'))}
        </button>
      </div>

      <div class="plan-card plan-featured" class:plan-current={$userTier === 3}>
        <div class="plan-badge">Most Popular</div>
        <div class="plan-name">Full {#if $userTier === 3}<span class="plan-current-tag">Current</span>{/if}</div>
        <div class="plan-price">${$orgPricing.voice}<span class="plan-period">/mo per seat</span></div>
        <ul class="plan-features">
          <li>Everything in Base</li>
          <li>AI partner joins calls with full client + product knowledge</li>
          <li>Real-time call notes + follow-ups</li>
          <li>Unlimited call attendance</li>
          <li>Post-call briefs + action items</li>
        </ul>
        <button class="plan-cta plan-cta-primary" onclick={() => handleUpgrade(3)} disabled={loading || $userTier === 3}>
          {loading ? 'Redirecting...' : ($userTier === 3 ? 'Current Plan' : ($userTier >= 2 ? 'Switch to Full' : 'Get Started'))}
        </button>
      </div>
    </div>
  </div>
</div>
{/if}

<style>
  .upgrade-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
  }
  .upgrade-modal {
    background: var(--bg-surface, #fff);
    border-radius: 12px;
    padding: 32px;
    max-width: 640px;
    width: 90%;
    position: relative;
    box-shadow: 0 20px 60px rgba(0,0,0,0.2);
  }
  .upgrade-close {
    position: absolute;
    top: 12px;
    right: 16px;
    background: none;
    border: none;
    font-size: 22px;
    color: var(--text-muted);
    cursor: pointer;
    padding: 4px 8px;
  }
  .upgrade-close:hover { color: var(--text); }
  .plan-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }
  .plan-card {
    border: 1px solid var(--border);
    border-radius: 8px;
    padding: 24px 20px;
    display: flex;
    flex-direction: column;
    position: relative;
  }
  .plan-featured {
    border-color: #3b82f6;
    box-shadow: 0 0 0 1px #3b82f6;
  }
  .plan-current {
    opacity: 0.65;
  }
  .plan-current-tag {
    display: inline-block;
    margin-left: 8px;
    font-size: 10px;
    font-weight: 600;
    color: #22c55e;
    border: 1px solid #22c55e;
    border-radius: 8px;
    padding: 1px 6px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    vertical-align: middle;
  }
  .plan-badge {
    position: absolute;
    top: -10px;
    left: 50%;
    transform: translateX(-50%);
    background: #3b82f6;
    color: #fff;
    font-size: 10px;
    font-weight: 600;
    padding: 2px 12px;
    border-radius: 10px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  .plan-name {
    font-size: 18px;
    font-weight: 700;
    color: var(--text);
    margin-bottom: 4px;
  }
  .plan-price {
    font-size: 28px;
    font-weight: 700;
    color: var(--text);
    margin-bottom: 16px;
  }
  .plan-period {
    font-size: 13px;
    font-weight: 400;
    color: var(--text-muted);
  }
  .plan-features {
    list-style: none;
    padding: 0;
    margin: 0 0 20px 0;
    flex: 1;
  }
  .plan-features li {
    font-size: 12px;
    color: var(--text);
    padding: 4px 0;
    padding-left: 16px;
    position: relative;
  }
  .plan-features li::before {
    content: '\2713';
    position: absolute;
    left: 0;
    color: #22c55e;
    font-weight: 600;
  }
  .plan-cta {
    display: block;
    text-align: center;
    padding: 10px 20px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 600;
    text-decoration: none;
    border: 1px solid var(--border-strong);
    color: var(--text);
    background: transparent;
    cursor: pointer;
    transition: all 0.15s;
  }
  .plan-cta:hover {
    background: var(--bg-hover);
    border-color: var(--accent);
    color: var(--accent-ink);
  }
  .plan-cta-primary {
    background: #3b82f6;
    color: #fff;
    border-color: #3b82f6;
  }
  .plan-cta-primary:hover {
    background: #2563eb;
    border-color: #2563eb;
    color: #fff;
  }
</style>
